import * as React from 'react';
import MonacoEditor, { EditorConstructionOptions } from 'react-monaco-editor';
import * as monaco from "monaco-editor/esm/vs/editor/editor.api";
import { DiffReport, DiffReportOperation, SelectorChange } from './DiffReport';
import { RawDeltaJSON } from './RawDeltaJSON';
import { MonacoStyles } from './styling/MonacoStyles';
import { FileTabNames } from './material-ui/FilesContent';
import { EditStackElement } from './EditStackElement';
import { OperationContext } from './material-ui/OperationContext';
import { Operations } from './material-ui/NavigatorData';

export interface IDeltaResultProps {
  code: any; // string or object
  viewType: ViewType;
  info: string;
  jobMetadataTable?: JSX.Element;
  display: boolean;
  inActiveGroup: boolean;
  editorId: FileTabNames;
  standaloneEditor?: boolean;
  onUnmount: (model: EditorModel, editorId: FileTabNames) => void;
  editorModel: EditorModel | null;
  onTextChange: (editorId: FileTabNames, text: string) => void;
  onDidGetFocus: (editorId: FileTabNames) => void;
  monacoTheme: string;
}

interface IDeltaResultState {
  decorations: string[];
  editor: monaco.editor.IStandaloneCodeEditor | null;
  isNew: boolean;
  changeIndex: number;
}

export interface Position {
  line: number,
  char: number,
  line2: number,
  char2: number
}

export interface EditorModel {
  text: string,
  decorations: monaco.editor.IModelDecoration[] | undefined,
  scrollPosition: monaco.editor.INewScrollPosition | undefined,
  selectionRange: monaco.IRange | null | undefined,
  isDirty: boolean,
  hasError: boolean,
  changeCount?: number,
  selectorChangeLines: SelectorChange[],
  undoStack: any,
  changeIndex: number
}

export enum ViewType {
  Raw,
  DiffResult
}

export enum Theme {
  VSDark,
  VSLight
}

export interface Delta {
  dx_delta: DXDelta;
}

export interface DXDelta {
  A: string,
  B: string
}

const baseScrollPosition: monaco.editor.INewScrollPosition = { scrollLeft: 0, scrollTop: 0 }

export default class DeltaResult extends React.Component<IDeltaResultProps, IDeltaResultState> {

  constructor(props: IDeltaResultProps) {
    super(props);
    this.editorDidMount = this.editorDidMount.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onDidChangeCursorSelection = this.onDidChangeCursorSelection.bind(this);
    this.onDidGetFocus = this.onDidGetFocus.bind(this);
  }

  state: IDeltaResultState = {
    changeIndex: -1,
    decorations: [],
    editor: null,
    isNew: true
  }

  skipTextChangeCallback = false;
  private changeCount = this.props.editorModel?.changeCount;
  private selectorChangeLines = this.props.editorModel ? this.props.editorModel.selectorChangeLines : [];
  private operation = DiffReportOperation.Other;
  private cursorLineBeyondChange = false;
  private timeOutId: number | null = null;

  componentDidUpdate(prevProps: IDeltaResultProps) {
    let EOL = '\n';
    const indentSize = 4;
    const monacoTheme = prevProps.monacoTheme;
    const theme = (monacoTheme === 'light') ? Theme.VSLight : Theme.VSDark;
    this.skipTextChangeCallback = false;
    if (!this.state.editor || this.props.editorModel?.isDirty) {
      // do nothing if editor is NULL
      // do nothing if editor state isDirty is set true
    } else if (

      (prevProps.code !== this.props.code || (this.state.isNew))

    ) {
      this.skipTextChangeCallback = true;
      this.setState({ isNew: false, changeIndex: -1 });
      this.state.editor.setScrollPosition(baseScrollPosition);
      const operationContext: Operations = this.context.operationType;
      this.operation = operationContext === Operations.threeWayMerge ? DiffReportOperation.ThreeWayMerge : DiffReportOperation.Other;

      if (this.props.viewType === ViewType.Raw) {
        this.state.editor.setValue(this.props.code);
        const { deltaDecorations, selectorChangeLines } = RawDeltaJSON.getDecorations(theme, this.props.code);
        this.changeCount = selectorChangeLines.length;
        this.selectorChangeLines = selectorChangeLines;
        const decorations = this.state.editor.deltaDecorations(this.state.decorations, deltaDecorations);
        this.setState({ decorations: decorations });
      } else if (this.props.viewType === ViewType.DiffResult) {
        const { deltaObj, paramObj } = typeof this.props.code === 'string' ? DeltaResult.getDeltaObjectFromString(this.props.code) : DeltaResult.getDeltaObjectFromObject(this.props.code);

        const m = this.state.editor.getModel();
        if (m) {
          EOL = m.getEOL();
        }
        DiffReport.EOL = EOL;

        const diffReport = DiffReport.getDiffReportData(deltaObj, indentSize, this.operation);
        let { textContent, viewChangeLines, viewChangeWords, selectorChangeLines } = diffReport;
        this.changeCount = selectorChangeLines.length;
        this.selectorChangeLines = selectorChangeLines;
        const deltaDecorations: monaco.editor.IModelDeltaDecoration[] = [];
        viewChangeLines.forEach((line) => {
          RawDeltaJSON.pushDiffViewLineDec(theme, line, deltaDecorations);
        });
        viewChangeWords.forEach((word) => {
          MonacoStyles.pushDiffViewWordDec(word, deltaDecorations, theme);
        });
        this.state.editor.setValue(textContent);
        const lineDecorations = this.state.editor.deltaDecorations(this.state.decorations, deltaDecorations);
        this.setState({ decorations: lineDecorations });
      }

      let baseSelection: monaco.IRange = new monaco.Range(1, 1, 1, 1);
      this.state.editor.setSelection(baseSelection);
    } else {
    }
  }

  componentDidMount() {
  }

  componentWillUnmount() {
    if (this.state.editor) {
      const e = this.state.editor;
      const text = e.getValue();
      let hasError = false;
      if (this.props.viewType !== ViewType.DiffResult) {
        try {
          JSON.parse(text);
        } catch (error) {
          hasError = true;
        }
      }
      const sel = e.getSelection();
      const selStart = sel?.getStartPosition();
      const selEnd = sel?.getEndPosition();
      const savedDecorations = this.isWritableEditor(this.props) ? undefined : e.getModel()?.getAllDecorations();
      let selRange: monaco.IRange | null = null;
      if (sel && selStart && selEnd) {
        selRange = { startColumn: selStart.column, startLineNumber: selStart.lineNumber, endColumn: selEnd.column, endLineNumber: selEnd.lineNumber }
      }
      // monaco.irange
      const modelClone: EditorModel = {
        hasError: hasError,
        decorations: savedDecorations,
        text: text,
        scrollPosition: { scrollLeft: e.getScrollLeft(), scrollTop: e.getScrollTop() },
        selectionRange: selRange,
        isDirty: true, // force use of 'text' property when remounted
        undoStack: this.getUndoStack(),
        selectorChangeLines: this.selectorChangeLines,
        changeIndex: this.state.changeIndex
      };
      if (this.changeCount) {
        modelClone['changeCount'] = this.changeCount;
      }
      this.props.onUnmount(modelClone, this.props.editorId);
    }
  }

  getUndoStack() {
    if (this.state.editor) {
      const laxModel: any = this.state.editor.getModel();
      return ({
        history: JSON.stringify({
          currentOpenStackElement: laxModel['_commandManager'].currentOpenStackElement,
          past: laxModel['_commandManager'].past,
          future: laxModel['_commandManager'].future,
        })
      })
    }
  }

  setUndoStack(editor: monaco.editor.IStandaloneCodeEditor, undoStack: any) {
    if (!undoStack) {
      return;
    }
    const history = JSON.parse(undoStack.history);
    if (history) {
      const laxEditor: any = editor.getModel();
      if (history.currentOpenStackElement) {
        laxEditor['_commandManager']['currentOpenStackElement'] = new EditStackElement(history.currentOpenStackElement);
      }
      if (history.past && history.past.length) {
        laxEditor['_commandManager'].past.push(...history.past.map((data: any) => new EditStackElement(data)));
      }
      if (history.future && history.future.length) {
        laxEditor['_commandManager'].future.push(...history.future.map((data: any) => new EditStackElement(data)));
      }
    }
  }

  editorDidMount(editor: monaco.editor.IStandaloneCodeEditor, monacoType: typeof monaco) {
    if (this.props.editorId === FileTabNames.diffOutTab || this.props.editorId === FileTabNames.rawOutTab) {
      editor.addAction({
        id: 'next-change',
        label: 'Next Change',
        keybindings: [monaco.KeyMod.Alt | monaco.KeyCode.DownArrow],
        contextMenuGroupId: 'navigation',
        run: (ed) => {
          this.incChangeIndex(editor);
        }
      });
      editor.addAction({
        id: 'prev-change',
        label: 'Previous Change',
        keybindings: [monaco.KeyMod.Alt | monaco.KeyCode.UpArrow],
        contextMenuGroupId: 'navigation',
        run: (ed) => {
          this.decChangeIndex(editor);
        }
      });
    }
    const changeIndex = this.props.editorModel ? this.props.editorModel.changeIndex : -1;
    this.setState({ editor: editor, changeIndex: changeIndex });
    editor.onDidFocusEditorText(this.onDidGetFocus);
    if (!this.isWritableEditor(this.props)) {
      editor.onDidChangeCursorSelection(this.onDidChangeCursorSelection);
    }
    if (this.props.editorModel) {
      const { decorations, text, scrollPosition, selectionRange, undoStack } = this.props.editorModel;
      editor.setValue(text);
      if (decorations) {
        editor.deltaDecorations([], decorations)
      }
      if (scrollPosition) {
        editor.setScrollPosition(scrollPosition);
      }
      if (selectionRange) {
        editor.setSelection(selectionRange);
      }
      if (undoStack) {
        this.setUndoStack(editor, undoStack);
      }

    }
  }

  private incChangeIndex(editor: monaco.editor.IStandaloneCodeEditor) {
    if (this.changeCount && this.changeCount > 0) {
      const newChangeIndex = this.state.changeIndex + 1 < this.changeCount ? this.state.changeIndex + 1 : 0;
      this.cursorLineBeyondChange = false;
      this.selectChangeIndex(newChangeIndex, editor);
    }
  }

  private decChangeIndex(editor: monaco.editor.IStandaloneCodeEditor) {
    if (this.cursorLineBeyondChange) {
      this.cursorLineBeyondChange = false;
      this.selectChangeIndex(this.state.changeIndex, editor);
    } else if (this.changeCount && this.changeCount > 0) {
      let newChangeIndex = this.state.changeIndex > 0 ? this.state.changeIndex - 1 : this.changeCount - 1;
      this.selectChangeIndex(newChangeIndex, editor);
    }
  }

  private selectChangeIndex(newChangeIndex: number, editor: monaco.editor.IStandaloneCodeEditor) {
    const selectedChange = this.selectorChangeLines[newChangeIndex];
    const newLine = selectedChange.line;
    let newSelection: monaco.IRange = new monaco.Range(newLine, 1, newLine, 1);
    editor.setSelection(newSelection);
    editor.revealLine(newLine);
    this.setState({ changeIndex: newChangeIndex });
  }

  isWritableEditor(props: IDeltaResultProps) {
    return (this.props.editorId === FileTabNames.inputATab || this.props.editorId === FileTabNames.inputBTab || this.props.editorId === FileTabNames.ancestorInputTab)
  }

  onDidGetFocus = () => {
    this.props.onDidGetFocus(this.props.editorId);
  }

  onChange(value: string, event: monaco.editor.IModelContentChangedEvent) {
    if (this.skipTextChangeCallback) {
      this.skipTextChangeCallback = false;
    } else if (this.isWritableEditor(this.props)) {
      this.props.onTextChange(this.props.editorId, value);
    }
  }

  onDidChangeCursorSelection(e: monaco.editor.ICursorSelectionChangedEvent) {
    if (e.reason === monaco.editor.CursorChangeReason.Explicit) {
      if (this.timeOutId) {
        window.clearTimeout(this.timeOutId);
      }
        if (this.state.editor) {
          const lineNumber = e.selection.selectionStartLineNumber;
          this.cursorLineBeyondChange = false;
          let newChangeIndex = this.selectorChangeLines.length - 1;
          if (this.selectorChangeLines.length === 0) {
            return;
          } else if (lineNumber + 1 < this.selectorChangeLines[0].line) {
            this.setState({ changeIndex: -1 });
            return;
          } else {
            this.cursorLineBeyondChange = true;
            this.selectorChangeLines.every((item, index) => {
              if (item.line === lineNumber) {
                newChangeIndex = index;
                this.cursorLineBeyondChange = false;
                return false;
              } if (item.line > lineNumber) {
                newChangeIndex = index - 1;
                return false;
              } else {
                return true;
              }
            });
            this.setState({ changeIndex: newChangeIndex });
          }
        }
    }
  }

  public render() {
    if (this.props.display && this.props.inActiveGroup) {
      this.state.editor?.focus();
    }
    const options: EditorConstructionOptions = {
      selectOnLineNumbers: true,
      readOnly: !this.isWritableEditor(this.props),
      automaticLayout: true,
    };
    let changeWord = this.operation === DiffReportOperation.ThreeWayMerge ? 'Conflict' : 'Change';
    changeWord = this.changeCount === 1 ? changeWord : changeWord + 's';
    const changeText = this.changeCount ? ' - ' + this.changeCount + ' ' + changeWord : '';
    const editorInfoText = this.props.info + changeText;
    const tabbedEditor = !this.props.standaloneEditor;
    let footer = tabbedEditor ? editorInfoText : this.props.jobMetadataTable;
    if (this.state.changeIndex > -1 && tabbedEditor) {
      const sfx = this.cursorLineBeyondChange ? '+' : ''
      footer += ` (Selected: ${this.state.changeIndex + 1}${sfx})`
    }
    const flex3Class = tabbedEditor ? "flex3" : "standaloneFlex3";
    const monacoHeightOffset = tabbedEditor? 120 : 300;
    const monacoHeight = this.props.display? `calc(100vh - ${monacoHeightOffset}px)` : '0px';
    const footerElement = this.props.display? (<div className={flex3Class}>{footer}</div>) : null;

    return (
        <>
        <MonacoEditor
          width="100%"
          height={monacoHeight}
          language="json"
          theme={(this.props.monacoTheme === "light") ? 'vs-light' : 'vs-dark'}
          options={options}
          onChange={this.onChange}
          editorDidMount={this.editorDidMount}
        />
        {footerElement}
      </>
    );
  }

  public static getDeltaObjectFromString(fileText: string | null) {
    if (fileText !== null) {
      const obj = JSON.parse(fileText);
      const { deltaObj, paramObj } = DeltaResult.getDeltaObjectFromObject(obj);
      return { deltaObj, paramObj };
    } else {
      return { deltaObj: {}, paramObj: {} };
    }
  }

  public static getDeltaObjectFromObject(obj: any) {
    const rootObject = obj['dx_deltaJSON'];
    let deltaObj: any = {};
    let paramObj: any = {};
    if (rootObject) {
      deltaObj = rootObject['dx_deltaJSON_delta'];
      paramObj = rootObject['dx_deltaJSON_metadata'];
    } else {
      deltaObj = obj;
    }
    return { deltaObj, paramObj };
  }
}
DeltaResult.contextType = OperationContext;
